<template>
  <v-dialog v-model="dialog" max-width="1200px" persistent>
    <v-card>
      <v-card-title class="headline">
        {{ isEditing ? 'Editare cerere' : 'Cerere nouă pentru modificarea notelor' }}
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-container>
            <v-row>
              <!-- Group field moved to first position and made primary -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="request.grupa"
                  :items="groupsList"
                  item-text="Denumire"
                  item-value="Denumire"
                  label="Grupa *"
                  :rules="[v => !!v || 'Grupa este obligatorie']"
                  required
                  @input="handleGroupInput"
                  @change="handleGroupChange"
                  :loading="loadingGroupDetails"
                  :filter="customFilter"
                  :no-data-text="loadingGroups ? 'Se încarcă...' : 'Nu s-au găsit grupe'"
                  hint="Introduceți grupa pentru a auto-completa celelalte câmpuri"
                  persistent-hint
                ></v-autocomplete>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="request.semestru"
                  :items="availableSemesters"
                  label="Semestrul *"
                  :rules="[v => !!v || 'Semestrul este obligatoriu']"
                  required
                  :disabled="!request.grupa"
                  @change="handleSemesterChange"
                ></v-select>
              </v-col>
              
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="request.facultatea"
                  label="Facultatea *"
                  :rules="[v => !!v || 'Facultatea este obligatorie']"
                  required
                  readonly
                  :disabled="true"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="request.ciclu_de_studii"
                  label="Ciclul de studii *"
                  :rules="[v => !!v || 'Ciclul de studii este obligatoriu']"
                  required
                  readonly
                  :disabled="true"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="request.frecventa"
                  label="Frecvența *"
                  :rules="[v => !!v || 'Frecvența este obligatorie']"
                  required
                  readonly
                  :disabled="true"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="request.specialitatea"
                  label="Specialitatea *"
                  :rules="[v => !!v || 'Specialitatea este obligatorie']"
                  required
                  readonly
                  :disabled="true"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="request.cause_id"
                  :items="requestTypes"
                  item-text="name"
                  item-value="id"
                  label="Tipul cererii *"
                  :rules="[v => !!v || 'Tipul cererii este obligatorie']"
                  required
                ></v-select>
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="request.explicatia"
                  label="Explicația pentru modificarea notelor *"
                  hint="Introduceți motivul pentru care solicitați modificarea notelor"
                  rows="2"
                  :rules="[v => !!v || 'Explicația este obligatorie']"
                  required
                ></v-textarea>
              </v-col>
              
              <v-col cols="12">
                <v-card outlined class="mb-4">
                  <v-card-title class="subtitle-1">
                    Modificări note
                    <v-spacer></v-spacer>
                    <v-btn color="primary" small @click="addGradeChange" :disabled="!request.grupa || !request.semestru">
                      <v-icon left>mdi-plus</v-icon> Adăugare
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <v-data-table
                      :headers="gradeHeaders"
                      :items="request.gradeChanges"
                      hide-default-footer
                      :items-per-page="-1"
                    >
                      <template v-slot:item="{ item, index }">
                        <tr>
                          <td>{{ index + 1 }}</td>
                          <td>
                            <v-text-field
                              v-model="item.student_name"
                              label="Nume student"
                              dense
                              hide-details
                              required
                              :rules="[v => !!v || 'Nume student este obligatorie']"
                            ></v-text-field>
                          </td>
                          <td>
                            <v-select
                              v-model="item.disciplina_name"
                              :items="availableDisciplines"
                              label="Disciplina"
                              dense
                              hide-details
                              required
                              :rules="[v => !!v || 'Denumirea disciplinei este obligatorie']"
                              :loading="loadingDisciplines"
                            ></v-select>
                          </td>
                          <td>
                            <v-select
                              v-model="item.grade_type"
                              :items="getFilteredGradeTypes(item.disciplina_name, index)"
                              label="Tip notă"
                              dense
                              hide-details
                            ></v-select>
                          </td>                          <td>
                            <v-combobox
                              v-model="item.old_value"
                              :items="getGradeValueOptions()"
                              label="Valoare veche"
                              dense
                              hide-details
                              required
                              :rules="getGradeValidationRules('Valoarea veche')"
                              clearable
                              :no-data-text="'Introduceți o valoare numerică (1-10) sau selectați o opțiune'"
                              hint="Introduceți nota 1-10 sau selectați din listă"
                              persistent-hint
                              @input="validateAndCleanGradeValue($event, item, 'old_value')"
                            ></v-combobox>
                          </td>
                          <td>
                            <v-combobox
                              v-model="item.new_value"
                              :items="getGradeValueOptions()"
                              label="Valoare nouă"
                              dense
                              hide-details
                              required
                              :rules="getGradeValidationRules('Valoarea nouă')"
                              clearable
                              :no-data-text="'Introduceți o valoare numerică (1-10) sau selectați o opțiune'"
                              hint="Introduceți nota 1-10 sau selectați din listă"
                              persistent-hint
                              @input="validateAndCleanGradeValue($event, item, 'new_value')"
                            ></v-combobox>
                          </td>
                          <td>
                            <v-btn icon color="error" @click="removeGradeChange(index)">
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                    
                    <div v-if="request.gradeChanges.length === 0" class="text-center pa-4 grey--text">
                      Nu există modificări de note adăugate. Selectați grupa și semestrul, apoi apăsați butonul "Adăugare" pentru a adăuga o modificare.
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="closeDialog">Anulare</v-btn>
        <v-btn
          color="primary"
          :disabled="!valid || request.gradeChanges.length === 0"
          @click="saveRequest"
        >
          {{ isEditing ? 'Actualizare' : 'Salvare' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import { mapGetters } from 'vuex';

export default {
  name: 'TeacherRequestDialog',
  data() {
    return {
      dialog: false,
      valid: false,
      isEditing: false,
      request: {
        userId: null,
        facultatea: '',
        ciclu_de_studii: '',
        frecventa: '',
        specialitatea: '',
        grupa: '',
        semestru: '',
        disciplina: '',
        cause_id: null,
        explicatia: '',
        status_id: 13,
        gradeChanges: []
      },
      requestTypes: [],
      gradeTypes: [
        { id: 4, name: 'Nota Examen' },
        { id: 3, name: 'Media Curenta' },
        { id: 1, name: 'Evaluare Periodica' },
        { id: 2, name: 'Lucru Individual' },
        { id: 5, name: 'Colocviu' },
        { id: 6, name: 'Practica' }
      ],
      faculties: [
        'Facultatea de Litere',
        'Facultatea de Științe Reale, Economice și ale Mediului',
        'Facultatea de Ştiinţe ale Educaţiei, Psihologie şi Arte',
        'Facultatea de Drept şi Ştiinţe Sociale'
      ],
      studyCycles: [
        'Licență',
        'Masterat',
      ],
      frequencies: [
        'Frecvență la zi',
        'Frecvență redusă'
      ],
      semesters: [
        'Semestrul I',
        'Semestrul II',
        'Semestrul III',
        'Semestrul IV',
        'Semestrul V',
        'Semestrul VI',
        'Semestrul VII',
        'Semestrul VIII',
        'Semestrul IX',
        'Semestrul X'
      ],
      // New properties
      groupsList: [],
      loadingGroups: false,
      loadingGroupDetails: false,
      loadingDisciplines: false,
      availableSemesters: [],
      availableDisciplines: [],
      groupInputTimeout: null,
      currentGroupDetails: null,
      gradeHeaders: [
        { text: '#', value: 'index', width: '50px' },
        { text: 'Student', value: 'student_name' },
        { text: 'Disciplina', value: 'disciplina_name' },
        { text: 'Tip evaluare', value: 'grade_type' },
        { text: 'Valoare veche', value: 'old_value' },
        { text: 'Valoare nouă', value: 'new_value' },
        { text: 'Acțiuni', value: 'actions', width: '80px', sortable: false }
      ],
      specialGradeValues: ['Admis', 'Respins', 'Nu s-a prezentat'],
    };
  },
  computed: {
    ...mapGetters(['getUser']),
    // Same people as the "Creare Cerere" button (and the server rule)
    canCreate() {
      const role = this.getUser && this.getUser.role;
      return ['admin', 'profesor', 'rector', 'usersef'].includes(role) || !!(this.getUser && this.getUser.isTeacher);
    },
    isFreqRedusaDisabled() {
      return this.request.ciclu_de_studii === 'Masterat' || this.request.ciclu_de_studii === 'Doctorat';
    },
    filteredFrequencies() {
      if (this.isFreqRedusaDisabled) {
        return this.frequencies.filter(freq => freq !== 'Frecvență redusă');
      }
      return this.frequencies;
    },
    isPracticaRequest() {
      const requestTypeName = this.getRequestTypeName();
      return requestTypeName && requestTypeName.toLowerCase().includes('practica');
    },
    // Helper method to get specialties for the selected faculty
    availableSpecialtiesForFaculty() {
      if (!this.request.facultatea) return [];
      
      const facultyId = this.facultyIds[this.request.facultatea];
      if (!facultyId || !this.specialtiesByFaculty[facultyId]) return [];
      
      return this.specialtiesByFaculty[facultyId].map(specialty => specialty.name);
    },
    // New computed property to check if required fields are filled
    areRequiredFieldsFilled() {
      return !!(
        this.request.facultatea &&
        this.request.ciclu_de_studii &&
        this.request.frecventa &&
        this.request.specialitatea &&
        this.request.grupa
      );
    },
    // Check if we should show special grade values (Admis, Respins, etc)
    shouldShowSpecialValues() {
      // Show special values for particular request types
      return [20, 21, 22].includes(this.request.cause_id);
    }
  },
  watch: {
    'request.ciclu_de_studii': {
      handler: function(newCycle) {
        // Don't make changes if a group is already selected
        if (this.request.grupa) return;
        
        if (newCycle === 'Masterat' || newCycle === 'Doctorat') {
          this.$nextTick(() => {
            this.request.frecventa = 'Frecvență la zi';
          });
        }
        
        // Update specialties based on cycle type - only if no group is selected
        if (newCycle === 'Masterat') {
          this.updateMasterSpecialties();
        } else {
          const facultyId = this.facultyIds[this.request.facultatea];
          if (facultyId) {
            this.filteredSpecialties = this.specialtiesByFaculty[facultyId]?.map(specialty => specialty.name) || [];
          }
        }
        
        this.updateGradeTypesForAllChanges();
      },
      immediate: true
    },
    'request.cause_id': {
      handler: function() {
        this.updateGradeTypesForAllChanges();
      }
    },
    'request.disciplina': {
      handler: function() {
        this.updateGradeTypesForAllChanges();
      }
    },
    isPracticaRequest: {
      handler: function() {
        this.updateGradeTypesForAllChanges();
      }
    },
    'request.facultatea'(newVal) {
      // Don't make changes if a group is already selected
      if (this.request.grupa) return;
      
      if (newVal) {
        this.filteredSpecialties = this.availableSpecialtiesForFaculty();
        
        // Clear specialty if it's no longer valid for the selected faculty
        if (this.request.specialitatea && !this.filteredSpecialties.includes(this.request.specialitatea)) {
          this.request.specialitatea = '';
        }
      } else {
        this.filteredSpecialties = [];
        this.request.specialitatea = '';
      }
    },
    'request.grupa'(newVal) {
      if (newVal) {
        // When grup is selected, attempt to lock fields
        this.isFieldLocked = true;
      } else {
        // When grup is cleared, unlock fields
        this.isFieldLocked = false;
      }
    },
  },
  created() {
    if (this.canCreate) this.fetchGroups();
  },
  methods: {
    async open(item = null) {
      this.resetForm();
      this.isEditing = !!item;
      
      if (item) {
        try {
          const requestDetails = await api.getTeacherRequestById(item.requestId);
          this.request = { ...requestDetails };
          
          // If editing, load group details and disciplines
          if (this.request.grupa) {
            await this.loadGroupDetailsAndSetFields(this.request.grupa);
            if (this.request.semestru) {
              await this.loadDisciplinesForSemester();
            }
          }
        } catch (error) {
          console.error('Error loading request details:', error);
        }
      } else {
        this.request.userId = this.getUser.userId;
      }
      
      await this.loadRequestTypes();
      this.dialog = true;
    },
    
    closeDialog() {
      this.dialog = false;
      this.$nextTick(() => {
        this.resetForm();
      });
    },
    
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
      
      this.request = {
        userId: this.getUser.userId,
        facultatea: '',
        ciclu_de_studii: '',
        frecventa: '',
        specialitatea: '',
        grupa: '',
        semestru: '',
        disciplina: '',
        cause_id: null,
        explicatia: '',
        status_id: 13,
        gradeChanges: []
      };
    },
    
    async loadRequestTypes() {
      try {
        const allRequestTypes = await api.fetchRequestTypes();
        // Filter to get the grade-related request types (IDs 19, 20, 21, 22)
        this.requestTypes = allRequestTypes.filter(type => [19, 20, 21, 22].includes(type.id));
        
        if (this.requestTypes.length === 0) {
          console.error('No grade-related request types found!', allRequestTypes);
        }
      } catch (error) {
        console.error('Error loading request types:', error);
      }
    },
    
    async handleGroupInput(groupName) {
      // Clear timeout if user is still typing
      if (this.groupInputTimeout) {
        clearTimeout(this.groupInputTimeout);
      }
      
      // Only fetch details if we have a reasonable group name
      if (!groupName || groupName.length < 2) {
        this.clearGroupDependentFields();
        return;
      }
      
      // Debounce the API call
      this.groupInputTimeout = setTimeout(async () => {
        await this.loadGroupDetailsAndSetFields(groupName);
      }, 500);
    },
    
    async handleGroupChange(groupName) {
      if (!groupName) {
        this.clearGroupDependentFields();
        return;
      }
      
      await this.loadGroupDetailsAndSetFields(groupName);
    },
    
    async loadGroupDetailsAndSetFields(groupName) {
      if (!groupName) return;
      
      this.loadingGroupDetails = true;
      
      try {
        const groupDetails = await api.fetchGroupDetails(groupName);
        
        if (groupDetails) {
          this.currentGroupDetails = groupDetails;
          
          // Map the API response to our form fields
          this.request.facultatea = groupDetails.Facultatea || '';
          this.request.ciclu_de_studii = this.mapCicluDeStudii(groupDetails.CicluDeStudii);
          this.request.frecventa = this.mapFrecventa(groupDetails.Frecventa);
          this.request.specialitatea = groupDetails.Specialitatea || '';
          
          // Calculate available semesters based on current course and date
          this.calculateAvailableSemestersFromGroup(groupDetails);
          
          // Clear semester selection if it's not in available range
          if (this.request.semestru && !this.availableSemesters.some(s => s.value === this.request.semestru)) {
            this.request.semestru = '';
            this.availableDisciplines = [];
          }
          
        } else {
          console.warn('Group details not found for:', groupName);
          this.clearGroupDependentFields();
        }
      } catch (error) {
        console.error('Error loading group details:', error);
        this.clearGroupDependentFields();
      } finally {
        this.loadingGroupDetails = false;
      }
    },
    
    mapCicluDeStudii(ciclu) {
      if (!ciclu) return '';
      
      // Handle various spellings and encodings
      const normalized = ciclu.toLowerCase().replace(/[^a-z]/g, '');
      
      if (normalized.includes('licent') || normalized.includes('licen')) {
        return 'Licență';
      } else if (normalized.includes('master')) {
        return 'Masterat';
      } else if (normalized.includes('doctor')) {
        return 'Doctorat';
      }
      
      return ciclu; // Return original if we can't map it
    },
    
    mapFrecventa(frecventa) {
      if (!frecventa) return '';
      
      const normalized = frecventa.toLowerCase();
      
      if (normalized === 'zi') {
        return 'Frecvență la zi';
      } else if (normalized === 'ff' || normalized.includes('redus')) {
        return 'Frecvență redusă';
      }
      
      return frecventa; // Return original if we can't map it
    },
    
    calculateAvailableSemestersFromGroup(groupDetails) {
      if (!groupDetails || !groupDetails.Cursul) {
        this.availableSemesters = [];
        return;
      }
      
      const currentCourse = groupDetails.Cursul;
      const today = new Date();
      const currentYear = today.getFullYear();
      
      // Check if we're after May 31st
      const isAfterMay31 = today > new Date(currentYear, 4, 31); // Month is 0-indexed
      
      // Calculate maximum semester
      let maxSemester = currentCourse * 2;
      
      // If we're before May 31st, reduce by 1 semester
      if (!isAfterMay31) {
        maxSemester = maxSemester - 1;
      }
      
      // Generate available semesters
      this.availableSemesters = [];
      for (let i = 1; i <= maxSemester; i++) {
        this.availableSemesters.push({
          text: `Semestrul ${this.intToRoman(i)}`,
          value: i
        });
      }
    },
    
    intToRoman(num) {
      const romans = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
      return romans[num] || num.toString();
    },
    
    async handleSemesterChange() {
      if (this.request.grupa && this.request.semestru) {
        await this.loadDisciplinesForSemester();
      } else {
        this.availableDisciplines = [];
      }
    },
    
    async loadDisciplinesForSemester() {
      if (!this.request.grupa || !this.request.semestru) {
        this.availableDisciplines = [];
        return;
      }
      
      this.loadingDisciplines = true;
      
      try {
        const disciplines = await api.fetchGroupDisciplines(this.request.grupa, this.request.semestru);
        
        // Extract only the discipline names
        this.availableDisciplines = disciplines.map(d => d.Denumire).filter(name => name);
        
        // Clear grade changes if disciplines changed
        this.request.gradeChanges = [];
        
      } catch (error) {
        console.error('Error loading disciplines:', error);
        this.availableDisciplines = [];
      } finally {
        this.loadingDisciplines = false;
      }
    },
    
    clearGroupDependentFields() {
      this.request.facultatea = '';
      this.request.ciclu_de_studii = '';
      this.request.frecventa = '';
      this.request.specialitatea = '';
      this.request.semestru = '';
      this.availableSemesters = [];
      this.availableDisciplines = [];
      this.request.gradeChanges = [];
      this.currentGroupDetails = null;
    },
    
    async fetchGroups() {
      this.loadingGroups = true;
      try {
        // Replace direct API call with our proxied API method
        const groups = await api.fetchGroups();
        this.groupsList = groups || [];
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        this.loadingGroups = false;
      }
    },
    
    customFilter(item, queryText) {
      // Custom filter for autocomplete that only shows groups starting with the query
      const text = item.Denumire.toLowerCase();
      const query = queryText.toLowerCase();
      return text.indexOf(query) === 0; // Only match from the beginning
    },
    
    customFilterGrade(item, queryText) {
      if (!queryText) return true;
      
      // If it's an exact match of one of our special values, prioritize it
      if (this.specialGradeValues.some(v => v.toLowerCase() === queryText.toLowerCase())) {
        return item.toLowerCase() === queryText.toLowerCase();
      }
      
      // Otherwise do a contains search to show potential matches
      return item.toLowerCase().indexOf(queryText.toLowerCase()) > -1;
    },
    
    addGradeChange() {
      if (!this.request.grupa || !this.request.semestru) {
        this.$emit('error', 'Selectați grupa și semestrul înainte de a adăuga modificări de note.');
        return;
      }
      
      let defaultGradeType;
      if (this.request.gradeChanges.length > 0) {
        // Use the grade type and disciplina from the last added row
        defaultGradeType = this.request.gradeChanges[this.request.gradeChanges.length - 1].grade_type;
        const defaultDisciplina = this.request.gradeChanges[this.request.gradeChanges.length - 1].disciplina_name;
        
        this.request.gradeChanges.push({
          student_name: '',
          disciplina_name: defaultDisciplina || '',
          grade_type: defaultGradeType,
          old_value: '',
          new_value: ''
        });
      } else {
        const availableTypes = this.getFilteredGradeTypes();
        defaultGradeType = availableTypes[0];
        
        this.request.gradeChanges.push({
          student_name: '',
          disciplina_name: '',
          grade_type: defaultGradeType,
          old_value: '',
          new_value: ''
        });
      }
    },
    
    removeGradeChange(index) {
      this.request.gradeChanges.splice(index, 1);
    },
    
    async saveRequest() {
      if (!this.$refs.form.validate()) return;
      
      try {
        // Convert semestru from string format (e.g. "Semestrul I") to number (e.g. 1)
        if (typeof this.request.semestru === 'string' && this.request.semestru.startsWith('Semestrul ')) {
          // Extract the Roman numeral and convert to number
          const romanNumeral = this.request.semestru.replace('Semestrul ', '');
          this.request.semestru = this.romanToInt(romanNumeral);
        }
        
        // Set the disciplina field from the first grade change disciplina_name
        if (this.request.gradeChanges && this.request.gradeChanges.length > 0) {
          this.request.disciplina = this.request.gradeChanges[0].disciplina_name || '';
          
          this.request.gradeChanges = this.request.gradeChanges.map(change => {
            return {
              student_name: change.student_name,
              disciplina_name: change.disciplina_name,
              grade_type_id: this.getGradeTypeId(change.grade_type),
              old_value: change.old_value || '',
              new_value: change.new_value
            };
          });
        }

        if (this.isEditing) {
          const requestId = this.request.id;
          await api.updateTeacherRequest(requestId, this.request);
          this.$emit('success', 'Cererea a fost actualizată cu succes!');
        } else {
          await api.createTeacherRequest(this.request);
          this.$emit('success', 'Cererea a fost creată cu succes!');
        }
        
        this.$emit('update');
        this.dialog = false;
      } catch (error) {
        console.error('Error saving request:', error);
        this.$emit('error', 'A apărut o eroare la salvarea cererii.');
      }
    },
    
    getGradeTypeId(typeName) {
      const gradeType = this.gradeTypes.find(gt => gt.name === typeName);
      return gradeType ? gradeType.id : 4; // Default to Nota Examen if not found
    },
    handleCycleChange() {
      // Don't make changes if a group is already selected
      if (this.request.grupa) return;
      
      if (this.request.ciclu_de_studii === 'Masterat' || this.request.ciclu_de_studii === 'Doctorat') {
        // Fix: Set to 'Frecvență la zi' instead of just 'Zi'
        this.request.frecventa = 'Frecvență la zi';
        
        // Update specialties list based on selected faculty when switching to master
        if (this.request.facultatea) {
          this.updateMasterSpecialties();
        }
      } else {
        // Update the specialty list for bachelor's when switching back
        if (this.request.facultatea) {
          const facultyId = this.facultyIds[this.request.facultatea];
          if (facultyId) {
            this.filteredSpecialties = this.specialtiesByFaculty[facultyId]?.map(specialty => specialty.name) || [];
          }
        }
      }
    },
    getFilteredGradeTypes() {
      const requestTypeId = this.request.cause_id;
      
      if (!requestTypeId) {
        return this.gradeTypes.map(gt => gt.name);
      }
      
      // Modificarea notei de la examen
      if (requestTypeId === 20) {
        return ['Nota Examen'];
      }
      
      // Modificarea colocviului
      else if (requestTypeId === 21) {
        return ['Colocviu'];
      }
      
      // Modificarea notei de la practica
      else if (requestTypeId === 22) { // Changed from 22 to 22 to match the correct ID
        return ['Practica'];
      }
      
      // Modificarea notelor semestriale
      else if (requestTypeId === 19) {
        if (this.request.ciclu_de_studii === 'Masterat') {
          return ['Media Curenta', 'Evaluare Periodica', 'Lucru Individual'];
        }
        else {
          return ['Media Curenta', 'Evaluare Periodica', 'Lucru Individual'];
        }
      }
        return this.gradeTypes.map(gt => gt.name);
    },
      getGradeValueOptions() {
      // Always include special values and numeric grades 1-10
      const numericGrades = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
      
      // Combine special values with numeric grades
      return [...this.specialGradeValues, ...numericGrades];
    },
    
    getGradeValidationRules(fieldName) {
      return [
        v => !!v || `${fieldName} este obligatorie`,
        v => this.isValidGradeValue(v) || 'Valoarea trebuie să fie între 1-10 sau una din opțiunile speciale (Admis, Respins, Nu s-a prezentat)'
      ];
    },
    
    isValidGradeValue(value) {
      if (!value) return false;
      
      // Check if it's one of the special values
      if (this.specialGradeValues.includes(value)) {
        return true;
      }
      
      // Check if it's a valid numeric value
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return false;
      }
      
      // Must be between 1 and 10 (inclusive)
      return numValue >= 1 && numValue <= 10;
    },
    
    validateAndCleanGradeValue(value, item, field) {
      if (!value) return;
      
      // If it's a special value, keep it as is
      if (this.specialGradeValues.includes(value)) {
        return;
      }
      
      // Try to parse as number
      const numValue = parseFloat(value);
      
      // If it's not a number, clear the field
      if (isNaN(numValue)) {
        this.$nextTick(() => {
          item[field] = '';
        });
        return;
      }
      
      // Clamp the value between 1 and 10
      let clampedValue = Math.max(1, Math.min(10, numValue));
      
      // Round to 2 decimal places to handle floating point precision
      clampedValue = Math.round(clampedValue * 100) / 100;
      
      // Update the field if the value was changed
      if (clampedValue !== numValue) {
        this.$nextTick(() => {
          item[field] = clampedValue.toString();
        });
      }
    },
    
    getRequestTypeName() {
      if (!this.request.cause_id || !this.requestTypes.length) return null;
      
      const requestType = this.requestTypes.find(type => type.id === this.request.cause_id);
      return requestType ? (requestType.name || requestType.type) : null;
    },
    
    updateGradeTypesForAllChanges() {
      if (!this.request.gradeChanges || !this.request.gradeChanges.length) return;
      
      const availableTypes = this.getFilteredGradeTypes();
      
      this.request.gradeChanges.forEach(change => {
        if (!availableTypes.includes(change.grade_type)) {
          change.grade_type = availableTypes[0];
        }
      });
    },
    // Helper method to convert Roman numerals to integers
    romanToInt(roman) {
      const romanNumerals = {
        'I': 1,
        'II': 2,
        'III': 3,
        'IV': 4,
        'V': 5,
        'VI': 6,
        'VII': 7,
        'VIII': 8,
        'IX': 9,
        'X': 10
      };
      
      return romanNumerals[roman] || 1; // Default to 1 if conversion fails
    },
    async fetchGroups() {
      this.loadingGroups = true;
      try {
        // Replace direct API call with our proxied API method
        const groups = await api.fetchGroups();
        this.groupsList = groups || [];
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        this.loadingGroups = false;
      }
    },
    
    customFilter(item, queryText) {
      // Custom filter for autocomplete that only shows groups starting with the query
      const text = item.Denumire.toLowerCase();
      const query = queryText.toLowerCase();
      return text.indexOf(query) === 0; // Only match from the beginning
    },
    
    customFilterGrade(item, queryText) {
      if (!queryText) return true;
      
      // If it's an exact match of one of our special values, prioritize it
      if (this.specialGradeValues.some(v => v.toLowerCase() === queryText.toLowerCase())) {
        return item.toLowerCase() === queryText.toLowerCase();
      }
      
      // Otherwise do a contains search to show potential matches
      return item.toLowerCase().indexOf(queryText.toLowerCase()) > -1;
    },
  }
};
</script>

<style scoped>
.v-data-table::v-deep td {
  padding: 8px 16px !important;
}
</style>